import { useMemo } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { seedFromHex, getTitle, getTapeVariant } from '@/lib/polaroid-utils';

interface HandwrittenNoteProps {
  event: NostrEvent;
  /** Which side of the feed this note appears in */
  side: 'left' | 'right';
  /** Called when the note is tapped — opens the photo lightbox */
  onClick?: () => void;
}

/** Post-it note style — for short captions (≤6 words) */
const POSTIT_BG = '#FFF3C4';
const POSTIT_TEXT = '#2D1B4E';
const POSTIT_PEEL_UNDER = '#EDE498';

/** Notebook paper style — for long captions (>6 words) */
const PAPER_BG = '#FFFFFF';
const PAPER_TEXT = '#2D1B4E';
const PAPER_PEEL_UNDER = '#E8E8E8';
const RULED_LINES = 'repeating-linear-gradient(to bottom, transparent, transparent 17px, rgba(180,200,220,0.25) 17px, rgba(180,200,220,0.25) 18px)';

/** Tape solid colors — 8 variants, reference CSS variables so they adapt to active theme */
const TAPE_SOLID_VARS = [
  'var(--tape-0-solid)',
  'var(--tape-1-solid)',
  'var(--tape-2-solid)',
  'var(--tape-3-solid)',
  'var(--tape-4-solid)',
  'var(--tape-5-solid)',
  'var(--tape-6-solid)',
  'var(--tape-7-solid)',
];

/** Count words in a string */
function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/** Regex matching nostr:npub1... and nostr:nprofile1... references */
const NOSTR_MENTION_RE = /nostr:(npub1[023456789acdefghjklmnpqrstuvwxyz]{6,}|nprofile1[023456789acdefghjklmnpqrstuvwxyz]{6,})/g;

/** Extract the first pubkey hex from a nostr:npub1/nprofile1 mention, or null */
function decodeMentionPubkey(bech32: string): string | null {
  try {
    const decoded = nip19.decode(bech32);
    if (decoded.type === 'npub') return decoded.data;
    if (decoded.type === 'nprofile') return decoded.data.pubkey;
  } catch { /* ignore */ }
  return null;
}

/**
 * Replace nostr:npub1.../nprofile1... in a string with short placeholders
 * and return the list of unique pubkeys found.
 */
function preprocessMentions(text: string): { processed: string; pubkeys: string[] } {
  const pubkeys: string[] = [];
  const processed = text.replace(NOSTR_MENTION_RE, (_match, bech32: string) => {
    const pubkey = decodeMentionPubkey(bech32);
    if (pubkey) {
      if (!pubkeys.includes(pubkey)) pubkeys.push(pubkey);
      // Use a unique placeholder that won't appear in normal text
      return `\x00mention:${pubkey}\x00`;
    }
    // Couldn't decode — truncate the raw string so it doesn't overflow
    return bech32.slice(0, 12) + '…';
  });
  return { processed, pubkeys };
}

/** Inline mention tag that resolves a pubkey to @displayname */
function MentionTag({ pubkey, textColor }: { pubkey: string; textColor: string }) {
  const author = useAuthor(pubkey);
  const displayName = author.data?.metadata?.name ?? genUserName(pubkey);

  return (
    <span
      className="font-hand font-bold"
      style={{ color: textColor, opacity: 0.8 }}
    >
      @{displayName}
    </span>
  );
}

/** Split a snippet string on mention placeholders and render with MentionTag components */
function renderSnippetWithMentions(snippet: string, textColor: string): React.ReactNode[] {
  // Split on the \x00mention:pubkey\x00 placeholders
  const parts = snippet.split(/\x00mention:([0-9a-f]{64})\x00/);
  const nodes: React.ReactNode[] = [];

  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      // Plain text part
      if (parts[i]) nodes.push(parts[i]);
    } else {
      // Pubkey part
      nodes.push(<MentionTag key={`m-${i}`} pubkey={parts[i]} textColor={textColor} />);
    }
  }

  return nodes;
}

/**
 * A handwritten note stuck to the scrapbook page next to a polaroid.
 *
 * Short captions (≤6 words) → yellow post-it note
 * Long captions (>6 words) → white ruled notebook paper
 *
 * Both have sharp square corners, vertical tape strip, and a peeling corner.
 */
export function HandwrittenNote({ event, side, onClick }: HandwrittenNoteProps) {
  const title = getTitle(event);
  const rawText = title ? event.content : event.content;
  if (!rawText || rawText.trim().length < 8) return null;

  // Replace nostr:npub1... mentions with placeholders before truncating
  const { processed: noteText } = useMemo(() => preprocessMentions(rawText), [rawText]);

  const seed = seedFromHex(event.id, 20, 24);
  const rotation = ((seed % 12) - 6);

  // Tape color — uses CSS variable for theme adaptability
  const tapeSeed = getTapeVariant(event.id);
  const tapeColor = TAPE_SOLID_VARS[tapeSeed % TAPE_SOLID_VARS.length];
  const tapeRotation = ((seedFromHex(event.id, 4, 6) % 14) - 7);

  // Short → post-it, long → notebook paper
  const isShort = wordCount(noteText) <= 6;

  // Short notes (post-its) get less text, long notes (notebook paper) get more room
  const maxChars = isShort ? 80 : 220;
  const snippet = noteText.length > maxChars ? noteText.slice(0, maxChars) + '...' : noteText;
  const bg = isShort ? POSTIT_BG : PAPER_BG;
  const textColor = isShort ? POSTIT_TEXT : PAPER_TEXT;
  const peelUnder = isShort ? POSTIT_PEEL_UNDER : PAPER_PEEL_UNDER;

  return (
    <div
      className={onClick ? 'cursor-pointer' : 'pointer-events-none'}
      style={{
        transform: `rotate(${rotation}deg)`,
        maxWidth: '200px',
        minWidth: '110px',
      }}
      onClick={onClick}
    >
      <div className="relative">
        {/* Vertical tape strip */}
        <div
          className="absolute left-1/2 z-10"
          style={{
            width: '16px',
            height: '40px',
            top: '-30px',
            backgroundColor: tapeColor,
            opacity: 0.7,
            transform: `translateX(-50%) rotate(${tapeRotation}deg)`,
          }}
        />

        {/* The note body */}
        <div
          className="relative px-3 pt-4 pb-4"
          style={{
            backgroundColor: bg,
            ...(isShort ? {} : {
              backgroundImage: RULED_LINES,
              backgroundPosition: '0 8px',
            }),
            boxShadow: '1px 2px 4px rgba(0,0,0,0.08)',
          }}
        >
          <p
            className="font-hand text-[16px] leading-snug break-words overflow-hidden"
            style={{
              color: textColor,
              overflowWrap: 'anywhere',
            }}
          >
            {renderSnippetWithMentions(snippet, textColor)}
          </p>
        </div>

        {/* Bottom-right peeling corner */}
        <div
          className="absolute bottom-0 right-0 overflow-hidden"
          style={{ width: '20px', height: '20px' }}
        >
          <div
            className="absolute inset-0"
            style={{ backgroundColor: '#FDF6EC' }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, transparent 50%, ${peelUnder} 50%)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, transparent 45%, rgba(0,0,0,0.08) 50%, transparent 55%)',
            }}
          />
        </div>
      </div>
    </div>
  );
}
